import { useState, useEffect, useMemo, Dispatch, SetStateAction } from "react";
import { HiMiniArrowLongDown, HiMiniArrowLongUp } from "react-icons/hi2";

// Sorts a-z, z-a, 1-9, 9-1

// To use: add state in parent component for sorting 
// ex: const [sortedData, setSortedData] = useState(data);

type Props = {
  data: any[] | null | undefined;
  sortBy: string;
  onSorted?: Dispatch<SetStateAction<any[]>>;
  className?: string;
}

type sortConfig = {
  type: "alphabetical" | "numerical";
  direction: "asc" | "desc";
}

const Sort = ({ data, sortBy, onSorted, className }: Props) => {

  const [sortConfig, setSortConfig] = useState<sortConfig | null>({ type: "alphabetical", direction: "asc" });

  const sortedData = useMemo(() => {
    if (!data || !data.length || !sortConfig) return data;

    const sorted = [...data];

    sorted.sort((a, b) => {
      const isNumeric = (str: string) => /^[0-9]/.test(str.trim());
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      const aIsNumeric = isNumeric(aVal);
      const bIsNumeric = isNumeric(bVal);

      if (sortConfig.type === "alphabetical") {
        if (aIsNumeric && !bIsNumeric) return 1;
        if (!aIsNumeric && bIsNumeric) return -1;
        const result = aVal.localeCompare(bVal, undefined, { numeric: true });
        return sortConfig.direction === "asc" ? result : -result;
      }

      if (sortConfig.type === "numerical") {
        if (aIsNumeric && !bIsNumeric) return -1;
        if (!aIsNumeric && bIsNumeric) return 1;
        const result = aVal.localeCompare(bVal, undefined, { numeric: true });
        return sortConfig.direction === "asc" ? result : -result;
      }

      return 0;
    });

    return sorted;
  }, [data, sortBy, sortConfig]);

  useEffect(() => {
    if (onSorted && sortedData) {
      onSorted(sortedData);
    }
  }, [sortedData, onSorted]);

  const handleSort = (type: "alphabetical" | "numerical") => {
    setSortConfig((prev) => {
      if (prev?.type === type) {
        return { type, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { type, direction: "asc" };
    });
  };

  return (
    <div className={className + " flex space-x-1"}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()} // Prevents focus on click
        className="flex items-center space-x-1.5 my-1 pr-1.5 hover:bg-gray-100 active:bg-gray-200 rounded-md"
        onClick={() => handleSort("alphabetical")}
      >
        <div className="text-primary">
          {sortConfig?.type === "alphabetical" ? (
            sortConfig.direction === "asc" ?
            <HiMiniArrowLongDown size={20} className="text-primary" /> 
            : <HiMiniArrowLongUp size={20} className="text-secondary"/>
          ) : (
            <div className="w-[20px]" />
          )}
        </div>
        <div>
          <div>A</div>
          <div className="-mt-2">Z</div>
        </div>
      </button>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()} // Prevents focus on click
        className="flex items-center space-x-1 my-1 pr-1.5 hover:bg-gray-100 active:bg-gray-200 rounded-md"
        onClick={() => handleSort("numerical")}
      >
        <div>
          {sortConfig?.type === "numerical" ? (
            sortConfig.direction === "asc" ? 
              <HiMiniArrowLongDown size={20} className="text-primary" /> 
              : <HiMiniArrowLongUp size={20} className="text-secondary"/>
          ) : (
            <div className="w-[20px]" />
          )}
        </div>
        <div>
          <div>1</div>
          <div className="-mt-2">9</div>
        </div>
      </button>
    </div>
  )
}

export default Sort;